import { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  useFormik,
} from 'formik';
import * as yup from 'yup';
import {
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useResetPasswordMutation } from '../../../../graphql/generated/graphql';
import { setAlertDescription } from '../../../hooks/actions';
import { useNavigate } from "react-router-dom";

import { RecoverForm } from '../../../config/interfaces';
import OnBoardingLayout from '../../../theme/templates/on-boarding';

const RecoverPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [recover] = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loader, setLoader] = useState(false);

  const initialValues: RecoverForm = {
    password: '',
    confirmPassword: '',
  };

  const RecoverSchema = yup.object().shape({
    password: yup.string()
      .min(8, 'Too Short!')
      .required('Required'),
    confirmPassword: yup.string()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: RecoverSchema,
    onSubmit: async (variables) => {
      setLoader(true);
      try {
        await recover({
          variables: {
            token: params.token || '',
            password: variables.password,
            confirmPassword: variables.confirmPassword,
          },
        }).then(res => {
          navigate("/");
          setAlertDescription({
            open: true,
            message: "Your password has successfully been changed",
            severity: 'success',
          })
        });
      } catch (e) {
        console.error(e);
      }
      setLoader(false);
    },
  });

  return (
    <OnBoardingLayout>
      <Grid
        sx={{
          maxWidth: '90%',
          width: '500px',
          mb: 2,
          mx: 4,
          py: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
        item
      >
        <Typography
          component="h1"
          variant="h3"
          sx={{
            mb: 6,
            width: '100%',
            textAlign: 'center',
          }}
        >
          New Password
        </Typography>
        <Typography
          component="h1"
          variant="body1"
          sx={{
            mb: 2,
            textAlign: 'justify'
          }}
        >
          Enter and confirm your new password below.
          It must be at least 8 characters long.
          Then, click 'Recover password' to finish resetting your password.
        </Typography>
        <FormControl
          fullWidth
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirm-password"
            autoComplete="confirm-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            {loader ? <CircularProgress color="primary" /> : 'Recover password'}
          </Button>
        </FormControl>
      </Grid>
    </OnBoardingLayout>
  )
};

export default RecoverPage;
